import React from 'react'
import MuiTextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

interface TimeFieldSettings {
    disable_limit?: boolean
}

interface TimeFieldItem extends DisplayItemBase {
    settings?: TimeFieldSettings
}

interface Props extends BaseDisplayItemProps<string | null, TimeFieldItem> {
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

type State = {
    value: string | null
    hours: string
    minutes: string
}

/* ---------- Component ---------- */

class TimeField extends BaseDisplayItem<
    string | null,
    TimeFieldItem,
    HTMLInputElement
> {
    constructor(props: Props) {
        super(props)

        const valueStr = props.value ?? ''
        this.state = {
            value: props.value ?? null,
            hours: this.getHours(valueStr) ?? '',
            minutes: this.getMinutes(valueStr) ?? '',
        }

        this.onChangeHours = this.onChangeHours.bind(this)
        this.onChangeMinutes = this.onChangeMinutes.bind(this)
    }

    /* ---------- Value helpers ---------- */

    private isEmptyOrNumber(value: string) {
        const regExp = /^[0-9\b]+$/
        return value === '' || regExp.test(value)
    }

    private getSplitted(value: string) {
        if (!value || value.length <= 0) return undefined
        const parts = String(value).split(':')
        if (parts.length !== 2) return undefined
        return parts
    }

    private getHours(value: string) {
        const parts = this.getSplitted(value)
        return parts?.[0]
    }

    private getMinutes(value: string) {
        const parts = this.getSplitted(value)
        return parts?.[1]
    }

    private isLimitDisabled(): boolean {
        // Original behavior: if disable_limit === true => allow >23 hours
        return Boolean((this.props as Props).item?.settings?.disable_limit)
    }

    private buildTime(hours: string, minutes: string): string | null {
        if (!hours && !minutes) return null
        const h = hours || '00'
        const m = minutes || '00'
        return `${h}:${m}`
    }

    /* ---------- Overrides ---------- */

    getValue() {
        return this.buildTime(this.state.hours, this.state.minutes)
    }

    /* ---------- Change handlers ---------- */

    onChangeHours(event: React.ChangeEvent<HTMLInputElement>) {
        const nextHours = event.target.value

        if (!this.isEmptyOrNumber(nextHours)) return

        const n = nextHours === '' ? NaN : parseInt(nextHours, 10)
        if (!Number.isNaN(n)) {
            if (!this.isLimitDisabled() && (n > 23 || n < 0)) return
            if (this.isLimitDisabled() && n < 0) return
        }

        const nextMinutes = this.state.minutes ? this.state.minutes : '00'

        this.setState({ hours: nextHours, minutes: nextMinutes }, () => {
            const next = this.buildTime(this.state.hours, this.state.minutes)
            ;(this.props as Props).changeValue?.(
                (this.props as Props).item.id,
                next,
                (this.props as Props).item
            )
        })
    }

    onChangeMinutes(event: React.ChangeEvent<HTMLInputElement>) {
        const nextMinutes = event.target.value

        if (!this.isEmptyOrNumber(nextMinutes)) return

        const n = nextMinutes === '' ? NaN : parseInt(nextMinutes, 10)
        if (!Number.isNaN(n) && (n > 59 || n < 0)) return

        const nextHours = this.state.hours ? this.state.hours : '00'

        this.setState({ hours: nextHours, minutes: nextMinutes }, () => {
            const next = this.buildTime(this.state.hours, this.state.minutes)
            ;(this.props as Props).changeValue?.(
                (this.props as Props).item.id,
                next,
                (this.props as Props).item
            )
        })
    }

    /* ---------- Lifecycle ---------- */

    componentDidUpdate(prevProps: Props) {
        // Sync internal state if parent value changes
        if (prevProps.value !== (this.props as Props).value) {
            const valueStr = ((this.props as Props).value ?? '') as string
            this.setState({
                value: (this.props as Props).value ?? null,
                hours: this.getHours(valueStr) ?? '',
                minutes: this.getMinutes(valueStr) ?? '',
            })
        }
    }

    /* ---------- UI pieces ---------- */

    private renderTimeInputs(
        withKeyPress: boolean,
        focusHours: boolean,
        focusMinutes: boolean
    ) {
        const props = this.props as Props

        return (
            <Grid container alignItems="center" spacing={1}>
                <Grid size={{ xs: 'auto' }}>
                    <MuiTextField
                        size="small"
                        fullWidth
                        inputRef={focusHours ? this.innerRef : undefined}
                        value={this.state.hours}
                        onChange={this.onChangeHours}
                        onKeyPress={
                            withKeyPress && props.onKeyPress
                                ? (e) => props.onKeyPress!(props.item.id, e)
                                : undefined
                        }
                        error={this.isInvalid()}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                </Grid>

                <Grid size={{ xs: 'auto' }}>
                    <Box sx={{ px: 0.5 }}>
                        <Typography variant="body1">:</Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 'auto' }}>
                    <MuiTextField
                        size="small"
                        fullWidth
                        inputRef={focusMinutes ? this.innerRef : undefined}
                        value={this.state.minutes}
                        onChange={this.onChangeMinutes}
                        onKeyPress={
                            withKeyPress && props.onKeyPress
                                ? (e) => props.onKeyPress!(props.item.id, e)
                                : undefined
                        }
                        error={this.isInvalid()}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                </Grid>

                <Grid size={12}>
                    {this.isInvalid() ? (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5 }}
                        >
                            {this.getErrorText()}
                        </Typography>
                    ) : null}
                </Grid>
            </Grid>
        )
    }

    /* ---------- Render implementations ---------- */

    renderShow(): React.ReactNode {
        const props = this.props as Props
        if (!props.value) return null
        return props.value
    }

    renderEdit(): React.ReactNode {
        // mimic your original focus behavior:
        // - if both empty -> focus hours
        // - else if hours set and minutes empty -> focus minutes
        const focusHours = !this.state.hours && !this.state.minutes
        const focusMinutes = Boolean(this.state.hours && !this.state.minutes)

        return this.renderTimeInputs(false, focusHours, focusMinutes)
    }

    renderSearch(): React.ReactNode {
        // in search mode you previously attached onKeyPress to both inputs
        return this.renderTimeInputs(true, false, false)
    }
}

export default TimeField
